import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormHelperText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithPublicMode, getPublicModeParams } from '../utils/apiUtils';
import { PageBreadcrumbs } from '../components/PageBreadcrumbs';
import {
  ObjectFieldTree,
  type FieldChangeKind,
  type ObjectFieldDefinition,
} from '../components/objects/ObjectFieldTree';

interface ObjectSummary {
  id: string;
  name: string;
  display_name: string;
  description?: string | null;
  customer_id?: string;
}

interface ObjectDefinition {
  name: string;
  display_name?: string | null;
  description?: string | null;
  fields: ObjectFieldDefinition[];
}

interface EditTarget extends ObjectDefinition {
  id: string;
}

interface FieldChange {
  field: string;
  change: FieldChangeKind;
  detail: string;
}

interface GenerateResult {
  object: ObjectDefinition;
  changes?: FieldChange[] | null;
  notes?: string;
  source?: 'ai' | 'json';
}

interface SavedInfo {
  id: string;
  displayName: string;
  cascadedCount: number;
  isNewVersion: boolean;
}

type JsonStatus =
  | { state: 'none' }
  | { state: 'valid'; json: string; rest: string }
  | { state: 'invalid'; reason: string };

const OBJECT_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_\s]*$/;

const CHANGE_COLORS: Record<FieldChangeKind, 'success' | 'error' | 'warning'> = {
  added: 'success',
  removed: 'error',
  changed: 'warning',
};

const findBalancedEnd = (text: string, start: number): number => {
  const stack: string[] = [];
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === '{' || ch === '[') stack.push(ch);
    else if (ch === '}' || ch === ']') {
      const open = stack.pop();
      if ((ch === '}' && open !== '{') || (ch === ']' && open !== '[')) return -1;
      if (stack.length === 0) return i;
    }
  }
  return -1;
};

const isObjectSample = (value: unknown): boolean =>
  typeof value === 'object' && value !== null &&
  (!Array.isArray(value) || (value.length > 0 && typeof value[0] === 'object' && value[0] !== null));

const parseError = (candidate: string): string => {
  try {
    JSON.parse(candidate);
    return 'it must be an object or a list of objects';
  } catch (e) {
    return e instanceof Error ? e.message : 'could not be parsed';
  }
};

/** Find a JSON sample inside free text: a ```json fence, the whole text, or the first {...} / [{...}] block. */
export const analyzeInput = (text: string): JsonStatus => {
  const trimmed = text.trim();
  if (!trimmed) return { state: 'none' };

  const fence = /```(?:json)?[ \t]*\n([\s\S]*?)```/i.exec(text);
  if (fence) {
    const candidate = fence[1].trim();
    try {
      const value = JSON.parse(candidate);
      if (isObjectSample(value)) {
        return { state: 'valid', json: candidate, rest: text.replace(fence[0], '').trim() };
      }
    } catch {
      // reported below
    }
    return { state: 'invalid', reason: parseError(candidate) };
  }

  const start = text.search(/[{[]\s*["{[\]}]/);
  if (start === -1) return { state: 'none' };
  const end = findBalancedEnd(text, start);
  if (end === -1) {
    return { state: 'invalid', reason: parseError(text.slice(start).trim()) };
  }
  const candidate = text.slice(start, end + 1);
  try {
    const value = JSON.parse(candidate);
    if (!isObjectSample(value)) return { state: 'none' };
    return { state: 'valid', json: candidate, rest: `${text.slice(0, start)}\n${text.slice(end + 1)}`.trim() };
  } catch {
    return { state: 'invalid', reason: parseError(candidate) };
  }
};

const errorMessage = (body: any, status: number): string => {
  const detail = body?.detail ?? body?.error ?? body?.message;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => (typeof d === 'string' ? d : d?.msg)).filter(Boolean).join('; ');
  }
  if (status === 402) return 'You are out of AI credits. Add credits to keep generating.';
  return `Request failed (${status})`;
};

const highlightsFrom = (changes?: FieldChange[] | null): Record<string, FieldChangeKind> => {
  const out: Record<string, FieldChangeKind> = {};
  (changes || []).forEach((c) => {
    if (c.change !== 'removed') out[c.field] = c.change;
  });
  return out;
};

export const ObjectGenerator: React.FC = () => {
  const { user, permissions, isPublicMode } = useAuth();
  const isAdmin = permissions?.isAdmin || false;
  const [searchParams, setSearchParams] = useSearchParams();
  const objectParam = searchParams.get('object');

  const [input, setInput] = useState(() => searchParams.get('prompt') || '');
  const [objects, setObjects] = useState<ObjectSummary[]>([]);
  const [objectsLoading, setObjectsLoading] = useState(false);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [usageCount, setUsageCount] = useState<number | null>(null);

  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [draft, setDraft] = useState<ObjectDefinition | null>(null);
  const [refineText, setRefineText] = useState('');

  const [cascade, setCascade] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<SavedInfo | null>(null);

  const jsonStatus = useMemo(() => analyzeInput(input), [input]);
  const isEditMode = Boolean(editTarget);

  const apiFetch = useCallback(
    async (url: string, init: RequestInit = {}) => {
      const idToken = await user?.getIdToken();
      const headers: Record<string, string> = { Authorization: `Bearer ${idToken}` };
      if (init.body) headers['Content-Type'] = 'application/json';
      const res = await fetchWithPublicMode(url, { ...init, headers }, isPublicMode, isAdmin);
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(errorMessage(body, res.status));
      return body;
    },
    [user, isPublicMode, isAdmin]
  );

  useEffect(() => {
    if (!user) return;
    setObjectsLoading(true);
    const params = new URLSearchParams({ ...getPublicModeParams(isPublicMode, isAdmin), page_size: '100' });
    apiFetch(`/api/v2/custom-objects/?${params}`)
      .then((body) => setObjects(Array.isArray(body?.results) ? body.results : []))
      .catch(() => setObjects([]))
      .finally(() => setObjectsLoading(false));
  }, [user, isPublicMode, isAdmin, apiFetch]);

  useEffect(() => {
    if (!user) return;
    if (!objectParam) {
      setEditTarget(null);
      setUsageCount(null);
      return;
    }
    let cancelled = false;
    setEditLoading(true);
    setError(null);
    Promise.all([
      apiFetch(`/api/v2/custom-objects/${objectParam}`),
      apiFetch(`/api/v2/custom-objects/${objectParam}/usage`).catch(() => null),
    ])
      .then(([objectBody, usageBody]) => {
        if (cancelled) return;
        const obj = objectBody?.data || objectBody;
        if (obj.customer_id === 'public' && !(isPublicMode && isAdmin)) {
          setEditTarget(null);
          setError('Public objects can\'t be edited. Pick one of your objects, or describe a new one.');
          return;
        }
        setEditTarget({
          id: obj.id,
          name: obj.name,
          display_name: obj.display_name,
          description: obj.description,
          fields: obj.fields || [],
        });
        setUsageCount(usageBody?.data?.function_count ?? null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setEditTarget(null);
        setError(`Could not load that object: ${e.message}`);
      })
      .finally(() => {
        if (!cancelled) setEditLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [objectParam, user, apiFetch, isPublicMode, isAdmin]);

  const referenceNames = useMemo(() => {
    const map: Record<string, string> = {};
    objects.forEach((o) => {
      map[o.id] = o.display_name || o.name;
    });
    return map;
  }, [objects]);

  const editableObjects = useMemo(
    () => objects.filter((o) => (isPublicMode && isAdmin) || o.customer_id !== 'public'),
    [objects, isPublicMode, isAdmin]
  );

  const resetResult = () => {
    setResult(null);
    setDraft(null);
    setRefineText('');
    setSaved(null);
    setSaveError(null);
    setError(null);
  };

  const selectObject = (value: ObjectSummary | null) => {
    resetResult();
    const next = new URLSearchParams(searchParams);
    if (value) next.set('object', value.id);
    else next.delete('object');
    next.delete('prompt');
    setSearchParams(next);
  };

  const applyResult = (data: GenerateResult) => {
    setResult(data);
    setDraft(data.object);
    setSaved(null);
    setSaveError(null);
  };

  const handleGenerate = async () => {
    if (!input.trim() || generating) return;
    const body: Record<string, unknown> = {};
    if (jsonStatus.state === 'valid') {
      body.json_sample = jsonStatus.json;
      if (jsonStatus.rest) body.prompt = jsonStatus.rest;
    } else {
      body.prompt = input.trim();
    }
    if (editTarget) body.object_id = editTarget.id;

    setGenerating(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v2/custom-objects/generate', { method: 'POST', body: JSON.stringify(body) });
      applyResult(res.data);
      setRefineText('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleRefine = async () => {
    if (!draft || !refineText.trim() || refining) return;
    setRefining(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v2/custom-objects/generate', {
        method: 'POST',
        body: JSON.stringify({
          prompt: refineText.trim(),
          object_id: editTarget?.id,
          current: draft,
        }),
      });
      applyResult(res.data);
      setRefineText('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Refinement failed');
    } finally {
      setRefining(false);
    }
  };

  const nameError = draft && !isEditMode && !OBJECT_NAME_PATTERN.test(draft.name.trim())
    ? 'Start with a letter; use letters, numbers, underscores or spaces'
    : null;

  const handleSave = async () => {
    if (!draft || saving || nameError) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (editTarget) {
        const params = new URLSearchParams({ create_version: 'true', cascade_to_functions: String(cascade) });
        const res = await apiFetch(`/api/v2/custom-objects/${editTarget.id}?${params}`, {
          method: 'PUT',
          body: JSON.stringify({
            display_name: draft.display_name,
            description: draft.description,
            fields: draft.fields,
          }),
        });
        setSaved({
          id: res.data.id,
          displayName: res.data.display_name,
          cascadedCount: (res.data.cascaded_functions || []).length,
          isNewVersion: true,
        });
        // The saved id is inactive now; further edits must start from the new version.
        const next = new URLSearchParams(searchParams);
        next.set('object', res.data.id);
        setSearchParams(next, { replace: true });
      } else {
        const res = await apiFetch('/api/v2/custom-objects/', {
          method: 'POST',
          body: JSON.stringify({
            name: draft.name.trim(),
            display_name: draft.display_name || draft.name,
            description: draft.description,
            is_searchable: true,
            fields: draft.fields,
          }),
        });
        setSaved({ id: res.data.id, displayName: res.data.display_name, cascadedCount: 0, isNewVersion: false });
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      action();
    }
  };

  const changes = result?.changes || [];
  const highlights = highlightsFrom(result?.changes);
  const selectedSummary = editTarget
    ? editableObjects.find((o) => o.id === editTarget.id) ||
      { id: editTarget.id, name: editTarget.name, display_name: editTarget.display_name || editTarget.name }
    : null;

  const startNew = () => {
    selectObject(null);
    setInput('');
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <PageBreadcrumbs
        items={[
          { label: 'Data & Outputs', to: '/dashboard/objects' },
          { label: editTarget ? editTarget.display_name || editTarget.name : 'Object Generator' },
        ]}
      />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h4">Object Generator</Typography>
        </Stack>
        <Button component={RouterLink} to="/dashboard/objects" startIcon={<ArrowBackIcon />} sx={{ flexShrink: 0 }}>
          Back to Data & Outputs
        </Button>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Describe the data you want, paste an example, or both, and get an object definition your AI functions can return.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Autocomplete
          options={editableObjects}
          loading={objectsLoading || editLoading}
          value={selectedSummary}
          onChange={(_, value) => selectObject(value)}
          getOptionLabel={(o) => o.display_name || o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={({ key: _key, ...props }, o) => (
            <li {...props} key={o.id}>
              <Box>
                <Typography variant="body2">{o.display_name || o.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {o.name}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Start from an existing object (optional)"
              helperText={isEditMode
                ? 'Editing: describe what to change. Existing field names and types are kept unless you ask to change them.'
                : 'Leave empty to create a new object.'}
            />
          )}
          sx={{ mb: 2 }}
        />

        {editTarget && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>
              Current definition: {editTarget.display_name || editTarget.name}
            </Typography>
            <ObjectFieldTree fields={editTarget.fields} referenceNames={referenceNames} />
          </Paper>
        )}

        <TextField
          fullWidth
          multiline
          minRows={6}
          maxRows={20}
          label="Describe the object, paste example JSON, or both"
          placeholder={isEditMode
            ? 'Example: add a list of line items with sku, quantity and unit price, and make status one of open, shipped, cancelled'
            : 'Example: an invoice with a number, issue date, customer (name, email) and line items. Or paste a JSON response here.'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, handleGenerate)}
          InputProps={{ sx: { fontFamily: jsonStatus.state === 'none' ? undefined : 'monospace' } }}
        />
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
          <Box>
            {jsonStatus.state === 'valid' && (
              <Chip
                icon={<CheckCircleIcon />}
                label={jsonStatus.rest ? 'JSON detected, plus a description' : 'JSON detected'}
                color="success"
                size="small"
                variant="outlined"
              />
            )}
            {jsonStatus.state === 'invalid' && (
              <Chip
                icon={<ErrorOutlineIcon />}
                label={`JSON looks invalid: ${jsonStatus.reason}`}
                color="warning"
                size="small"
                variant="outlined"
                sx={{ maxWidth: 600 }}
              />
            )}
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Ctrl/⌘ + Enter
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleGenerate}
              disabled={!input.trim() || generating || editLoading}
              startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
            >
              {generating ? 'Generating...' : isEditMode ? 'Generate changes' : 'Generate'}
            </Button>
          </Stack>
        </Stack>
        {jsonStatus.state === 'invalid' && (
          <FormHelperText>The text will be sent as a description. Fix the JSON to use it as an exact example.</FormHelperText>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && draft && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {isEditMode ? 'Proposed new version' : 'Generated object'}
          </Typography>

          {result.source === 'json' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The AI was unavailable, so this was built directly from your JSON. Review types and required fields.
            </Alert>
          )}
          {result.notes && result.source !== 'json' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {result.notes}
            </Alert>
          )}
          {result.notes && result.source === 'json' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {result.notes}
            </Typography>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Name"
              size="small"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              disabled={isEditMode || Boolean(saved)}
              error={Boolean(nameError)}
              helperText={isEditMode ? "An existing object's identifier can't change here" : nameError || 'Identifier used by functions'}
              InputProps={{ sx: { fontFamily: 'monospace' } }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Display name"
              size="small"
              value={draft.display_name || ''}
              onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
              disabled={Boolean(saved)}
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={draft.description || ''}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            disabled={Boolean(saved)}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle1" gutterBottom>
            Fields ({draft.fields.length})
          </Typography>
          <Box sx={{ mb: 3 }}>
            <ObjectFieldTree fields={draft.fields} highlights={highlights} referenceNames={referenceNames} />
          </Box>

          {result.changes && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {isEditMode ? 'Changes compared to the saved object' : 'Changes from the previous draft'}
              </Typography>
              {changes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No field changes.
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {changes.map((c) => (
                    <Stack key={`${c.change}-${c.field}`} direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={c.change}
                        size="small"
                        color={CHANGE_COLORS[c.change]}
                        sx={{ minWidth: 76, height: 22 }}
                      />
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {c.field}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {c.detail}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
              {changes.some((c) => c.change === 'removed' || c.detail.startsWith('type ')) && isEditMode && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  Removing or retyping fields can break functions that read them. Check the changes before saving.
                </Alert>
              )}
            </Box>
          )}

          {!saved && (
            <>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Refine
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }} sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={6}
                  size="small"
                  placeholder="Example: make price a decimal, add an optional discount code, drop the internal notes field"
                  value={refineText}
                  onChange={(e) => setRefineText(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleRefine)}
                />
                <Button
                  variant="outlined"
                  onClick={handleRefine}
                  disabled={!refineText.trim() || refining}
                  startIcon={refining ? <CircularProgress size={16} /> : <RefreshIcon />}
                  sx={{ whiteSpace: 'nowrap', minWidth: 120 }}
                >
                  {refining ? 'Refining...' : 'Refine'}
                </Button>
              </Stack>

              <Divider sx={{ mb: 2 }} />
              {isEditMode && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={<Checkbox checked={cascade} onChange={(e) => setCascade(e.target.checked)} />}
                    label="Also update functions that use this object"
                  />
                  <FormHelperText sx={{ mt: -0.5 }}>
                    {cascade
                      ? 'Every active function that uses this object gets a new version that returns the new structure.'
                      : 'Functions keep returning the current structure until you switch them to the new version.'}
                    {usageCount !== null && ` ${usageCount} function${usageCount === 1 ? '' : 's'} currently use${usageCount === 1 ? 's' : ''} this object.`}
                  </FormHelperText>
                </Box>
              )}
              {saveError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
                  {saveError}
                </Alert>
              )}
              <Button
                variant="contained"
                color="success"
                size="large"
                onClick={handleSave}
                disabled={saving || Boolean(nameError) || draft.fields.length === 0}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              >
                {saving ? 'Saving...' : isEditMode ? 'Save as new version' : 'Create object'}
              </Button>
            </>
          )}

          {saved && (
            <Alert severity="success">
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {saved.isNewVersion
                  ? `Saved a new version of ${saved.displayName}.`
                  : `Created ${saved.displayName}.`}
                {saved.cascadedCount > 0 &&
                  ` ${saved.cascadedCount} function${saved.cascadedCount === 1 ? ' was' : 's were'} updated to use it.`}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button component={RouterLink} to={`/dashboard/objects?edit=${saved.id}`} size="small" variant="contained">
                  View in Data & Outputs
                </Button>
                <Button size="small" variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={startNew}>
                  Generate another
                </Button>
                {isEditMode && (
                  <Button size="small" variant="outlined" onClick={() => { resetResult(); setInput(''); }}>
                    Make more changes
                  </Button>
                )}
                <Button component={RouterLink} to="/dashboard/ai-functions" size="small">
                  Use it in a function
                </Button>
              </Stack>
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ObjectGenerator;
